<script lang="ts">
  import { store } from '$lib/store.svelte';
  import { api } from '$lib/api';
  import type { Client } from '$shared/types';

  let name = $state('');
  let rate = $state<number | null>(null);
  let currency = $state('USD');
  let retainer = $state<number | null>(null);
  let creating = $state(false);
  let editing = $state<string | null>(null);

  async function create(event: SubmitEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    creating = true;
    try {
      await store.addClient(name.trim(), {
        rate: rate ?? undefined,
        currency: rate ? currency : undefined,
        retainer: retainer ?? undefined,
      });
      name = '';
      rate = null;
      retainer = null;
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    } finally {
      creating = false;
    }
  }

  async function save(client: Client, patch: Partial<Client>) {
    try {
      await store.saveClient(client.id, patch);
      editing = null;
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    }
  }

  async function rebuild() {
    try {
      store.clients = await api.rebuildClients();
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    }
  }

  function sheetUrl(id: string) {
    return `https://docs.google.com/spreadsheets/d/${id}/edit`;
  }
</script>

<div class="stack">
  <section class="card stack">
    <h2>Add a client</h2>
    <p class="muted fine">
      Creates a new spreadsheet in your Drive with Entries, Summary and Meta tabs.
    </p>
    <form class="stack" onsubmit={create}>
      <div>
        <label for="c-name">Name</label>
        <input id="c-name" bind:value={name} placeholder="Acme Corp" required />
      </div>
      <div class="grid">
        <div>
          <label for="c-rate">Hourly rate (optional)</label>
          <input id="c-rate" type="number" min="0" step="1" bind:value={rate} placeholder="—" />
        </div>
        <div>
          <label for="c-cur">Currency</label>
          <select id="c-cur" bind:value={currency}>
            <option>USD</option>
            <option>EUR</option>
            <option>GBP</option>
            <option>ILS</option>
          </select>
        </div>
        <div>
          <label for="c-ret">Retainer hours (optional)</label>
          <input id="c-ret" type="number" min="0" step="1" bind:value={retainer} placeholder="—" />
        </div>
      </div>
      <button class="primary" type="submit" disabled={creating || !name.trim()}>
        {creating ? 'Creating spreadsheet…' : 'Add client'}
      </button>
    </form>
  </section>

  <section class="stack">
    <div class="spread">
      <h2>Clients</h2>
      <button class="ghost fine" onclick={rebuild} title="Rescan Drive for app-created sheets">
        Rebuild from Drive
      </button>
    </div>

    {#each store.clients as client (client.id)}
      <div class="card client">
        <div class="spread">
          <div class="row">
            <span class="dot" style="background: {client.color}"></span>
            <strong>{client.name}</strong>
            {#if client.archived}<span class="tag">archived</span>{/if}
          </div>
          <div class="row">
            <a class="fine" href={sheetUrl(client.spreadsheetId)} target="_blank" rel="noreferrer">
              Open sheet ↗
            </a>
            <button
              class="ghost fine"
              onclick={() => (editing = editing === client.id ? null : client.id)}
            >
              {editing === client.id ? 'Close' : 'Edit'}
            </button>
          </div>
        </div>

        <div class="meta muted fine">
          {#if client.rate}{client.currency ?? ''} {client.rate}/h{:else}No rate set{/if}
          {#if client.retainer}· {client.retainer}h retainer{/if}
        </div>

        {#if editing === client.id}
          <div class="grid edit">
            <div>
              <label for="e-rate-{client.id}">Rate</label>
              <input
                id="e-rate-{client.id}"
                type="number"
                min="0"
                value={client.rate ?? ''}
                onchange={(e) =>
                  save(client, { rate: Number(e.currentTarget.value) || undefined })}
              />
            </div>
            <div>
              <label for="e-ret-{client.id}">Retainer</label>
              <input
                id="e-ret-{client.id}"
                type="number"
                min="0"
                value={client.retainer ?? ''}
                onchange={(e) =>
                  save(client, { retainer: Number(e.currentTarget.value) || undefined })}
              />
            </div>
            <div class="archive">
              <button class="ghost" onclick={() => save(client, { archived: !client.archived })}>
                {client.archived ? 'Unarchive' : 'Archive'}
              </button>
            </div>
          </div>
        {/if}
      </div>
    {/each}
  </section>
</div>

<style>
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.75rem;
  }

  .fine {
    font-size: 0.82rem;
  }

  .client {
    display: grid;
    gap: 0.5rem;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: none;
  }

  .tag {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--muted);
    border: 1px solid var(--line);
    border-radius: 999px;
    padding: 0.05rem 0.4rem;
  }

  .edit {
    border-top: 1px solid var(--line);
    padding-top: 0.75rem;
    align-items: end;
  }

  .archive {
    display: flex;
    align-items: end;
  }

  a {
    color: var(--accent);
  }
</style>
